import { Box, Flex } from '@chakra-ui/react';
import { Loader, Text } from '@kula-ai/components';
import { AlertCircle } from '@kula-ai/icons-core';
import { formatBytes } from '@/utils/format-bytes';
import { FILE_PARSE_FAILED } from '../../../constants';
import type { FileBlockData } from '../../../types';

interface FileBlockProps {
  data: FileBlockData;
}

// Uploaded-document status card (resume / offer letter / intake doc). Mirrors
// the StepTimeline status vocabulary: spinner while parsing, quiet green dot
// when parsed, red alert + message on failure.
export const FileBlock = ({ data }: FileBlockProps) => {
  if (!data?.name) return null;
  const size = formatBytes(data.sizeBytes);

  return (
    <Flex
      align="center"
      gap="8px"
      px="10px"
      py="8px"
      borderRadius="8px"
      borderWidth="1px"
      borderColor="var(--secondary-200)"
      maxW="420px"
      data-test-id="agentforge-file-block"
    >
      {data.status === 'parsing' && <Loader size={14} />}
      {data.status === 'parsed' && (
        <Box
          w="8px"
          h="8px"
          borderRadius="full"
          bg="green.400"
          flexShrink={0}
        />
      )}
      {data.status === 'failed' && (
        <Box color="red.500" flexShrink={0} display="inline-flex">
          <AlertCircle size={14} />
        </Box>
      )}
      <Flex direction="column" minW={0}>
        <Text variant="label" as="span" truncate>
          {data.name}
        </Text>
        <Text variant="small" as="span" className="text-secondary-500">
          {data.status === 'parsing' && 'Reading document…'}
          {data.status === 'parsed' && (size ? `Parsed · ${size}` : 'Parsed')}
          {data.status === 'failed' && (data.errorMessage || FILE_PARSE_FAILED)}
        </Text>
      </Flex>
    </Flex>
  );
};
